// draftUtils.js - Firebase draft management system
import { formatLanguageCode, normalizeNameForPath } from './storageUtils';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import app from '../../../firebase';
import { getFirebaseAuthToken } from '../../../utils/firebaseAuthToken';
import { prepareItineraryForPublication } from '../../services/automaticGuideResearch';

const ITINERARY_STORAGE_PROXY_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net/backendSaveItineraryJsonToFirebase';

/**
 * DRAFT STORAGE UTILITIES
 * ======================
 * Manages draft guides in Firebase Storage and Firestore
 */

// Generate draft filename with timestamp
const generateDraftFilename = (metadata) => {
  const safeCountry = normalizeNameForPath(metadata.country || '');
  const safeLanguage = formatLanguageCode(metadata.language);
  const normalizedItineraryName = normalizeNameForPath(metadata.name || '');
  const timestamp = Date.now();

  return `${safeCountry}_${safeLanguage}_${normalizedItineraryName}_draft_${timestamp}.json`;
};

// Generate draft storage path
const generateDraftStoragePath = (metadata, filename) => {
  const safeCountry = normalizeNameForPath(metadata.country || '');
  return `drafts/${safeCountry}/${filename}`;
};

// Save draft to Firebase Storage using Firebase SDK directly
export const saveDraftToStorage = async (itineraryData, metadata, userId = 'anonymous') => {
  try {
    const draftFilename = generateDraftFilename(metadata);
    const storagePath = generateDraftStoragePath(metadata, draftFilename);

    console.log('DEBUG: Saving draft to:', storagePath);
    console.log('DEBUG: Draft filename:', draftFilename);
    console.log('DEBUG: User ID:', userId);

    // Initialize Firebase Storage
    const storage = getStorage(app);
    const storageRef = ref(storage, storagePath);

    console.log('DEBUG: Itinerary data preview:', {
      metadata: itineraryData.metadata,
      pointsCount: itineraryData.points?.length || 0
    });

    // Convert itinerary data to JSON blob
    const jsonData = JSON.stringify(itineraryData, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });

    console.log('DEBUG: About to upload to Firebase Storage...');

    // Upload to Firebase Storage
    const uploadResult = await uploadBytes(storageRef, blob);
    console.log('DEBUG: Upload completed:', uploadResult);

    // Get the download URL
    const downloadURL = await getDownloadURL(uploadResult.ref);
    console.log('DEBUG: Download URL:', downloadURL);

    return {
      success: true,
      filename: draftFilename,
      storagePath: storagePath,
      storageUrl: downloadURL
    };

  } catch (error) {
    console.error('Error saving draft to storage:', error);
    throw new Error(`Failed to save draft: ${error.message}`);
  }
};

// Load draft from Firebase Storage using Firebase SDK
export const loadDraftFromStorage = async (storagePath) => {
  try {
    console.log('DEBUG: Loading draft from:', storagePath);

    // Initialize Firebase Storage
    const storage = getStorage(app);
    const storageRef = ref(storage, storagePath);

    // Get the download URL and fetch the content
    const downloadURL = await getDownloadURL(storageRef);
    console.log('DEBUG: Got download URL:', downloadURL);

    // Use multiple CORS proxy services as fallbacks
    const corsProxies = [
      'https://corsproxy.io/?',
      'https://cors-anywhere.herokuapp.com/',
      'https://api.allorigins.win/raw?url='
    ];

    let response;
    let lastError;

    for (const corsProxy of corsProxies) {
      try {
        const proxiedURL = corsProxy + encodeURIComponent(downloadURL);
        console.log('DEBUG: Trying CORS proxy:', corsProxy, 'with URL:', proxiedURL);

        response = await fetch(proxiedURL);

        if (response.ok) {
          console.log('DEBUG: Successfully fetched via proxy:', corsProxy);
          break;
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error) {
        console.warn('DEBUG: Proxy failed:', corsProxy, error);
        lastError = error;
        continue;
      }
    }

    if (!response || !response.ok) {
      throw lastError || new Error('All CORS proxies failed');
    }

    if (!response.ok) {
      throw new Error('Draft not found in storage');
    }

    const draftData = await response.json();
    console.log('DEBUG: Draft loaded successfully');
    return draftData;

  } catch (error) {
    console.error('Error loading draft from storage:', error);
    throw new Error(`Failed to load draft: ${error.message}`);
  }
};

// Delete draft from Firebase Storage
export const deleteDraftFromStorage = async (storagePath) => {
  try {
    // Note: This would require a backend function to delete from Firebase Storage
    // For now, we'll just mark it as deleted in Firestore
    console.log('Draft deletion requested for:', storagePath);
    return { success: true };
  } catch (error) {
    console.error('Error deleting draft from storage:', error);
    throw new Error(`Failed to delete draft: ${error.message}`);
  }
};

// Publish draft to live location
export const publishDraftToLive = async (draftMetadata, itineraryData) => {
  try {
    const publicationItinerary = prepareItineraryForPublication(itineraryData);
    // Use existing publish logic but with draft data
    const safeCountry = normalizeNameForPath(draftMetadata.country || '');
    const safeLanguage = formatLanguageCode(draftMetadata.language);
    const normalizedItineraryName = normalizeNameForPath(draftMetadata.title || '');
    const token = await getFirebaseAuthToken();

    const liveFilename = `${safeCountry}_${safeLanguage}_${normalizedItineraryName}.json`;
    const liveFolderName = `${safeCountry}_${safeLanguage}_${normalizedItineraryName}`;

    console.log('DEBUG: Publishing draft to live:', liveFilename);

    const response = await fetch(ITINERARY_STORAGE_PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        itineraryData: publicationItinerary,
        storageConfig: {
          country: draftMetadata.country,
          itineraryName: liveFolderName,
          filename: liveFilename,
          isDraft: false,
          forceUpdate: true
        }
      })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to publish draft');
    }

    return {
      success: true,
      filename: liveFilename,
      publicUrl: result.publicUrl || `https://storage.googleapis.com/ai-tourguide-1dc92.firebasestorage.app/special_itineraries/${safeCountry}/${liveFolderName}/${liveFilename}`
    };

  } catch (error) {
    console.error('Error publishing draft:', error);
    throw new Error(`Failed to publish draft: ${error.message}`);
  }
};

const draftUtils = {
  saveDraftToStorage,
  loadDraftFromStorage,
  deleteDraftFromStorage,
  publishDraftToLive,
  generateDraftFilename,
  generateDraftStoragePath
};

export default draftUtils;
